// Import Packages
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

// Import Material UI
import { Button, Dialog, IconButton, Tooltip, MenuItem, Box } from "@material-ui/core";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import InsertLinkIcon from '@material-ui/icons/InsertLink';
import Delete from "@material-ui/icons/Delete";
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';

// Import Services
import triggerService from "../../../../services/triggerService/triggerService";
import settingsService from "../../../../services/SettingsService/SettingsService";
import UserService from '../../../../services/UserService/UserService';

// Import Pages
import WebHookForm from "./WebHookForm";
import TextFieldInput from '../../../../components/TextField/TextField';
import DeleteWebhook from "../../webhookRule/deleteWebhook/DeleteWebhook";

import { BASE_URL } from "../../../../services/config"

// Import Recoil
import { useSetRecoilState } from 'recoil';
import { alertState } from '../../../../recoil-store/recoil-state';


// TabPanel styling
function TabPanel({ children, value, index }) {
    return <div>{value === index && <Box>{children}</Box>}</div>;
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

const action = {
    fields: {
        name: { value: "" },
        description: { value: "" },
        sourceValue: { value: "" },
        operator: { value: "" },
        targetValue: { value: "" },
        params: { value: "" },
    },
};

const useStyles = makeStyles({
    option: {
        '& > span': {
            marginRight: 10,
            fontSize: 12,
        },
        fontSize: 13,
        fontWeight: 600,
        color: '#46474E',
    }
});

const WebHook = (props) => {
    const { t } = useTranslation();
    const setAlert = useSetRecoilState(alertState);

    const [value, setValue] = useState(0);
    const [open, setOpen] = useState(false)
    const [rules, setRules] = useState([]);
    const [triggerProfile, setTriggerProfile] = useState([]);
    const [select, setSelect] = useState({});
    const [ruleList, setRuleList] = useState([]);
    const [webhookUrl, setWebhookUrl] = useState("");
    const classes = useStyles();
    useEffect(() => {
        action.fields.name.value = "";
        action.fields.description.value = "";
        action.fields.sourceValue.value = "";
        action.fields.targetValue.value = "";
        action.fields.operator.value = "";
        action.fields.params.value = "";
        fetchData().then(r => r);
    }, []);

    const fetchData = async () => {
        const key = await getAPIKey();
        // const url = await getSettings();

        setWebhookUrl(`${await BASE_URL}/${props.webhook}/run/${props.automId}?apiKey=${key}`);
        const responseList = await triggerService.getRules();
        if (responseList) {
            if (responseList?.data) {
                if (Array.isArray(responseList?.data)) {
                    setRuleList(responseList?.data);
                }
            }
        }
        const resp = await triggerService.getTriggerProfileByAutomId(
            props.automId,
            "WEBHOOK"
        );
        if (resp && resp.status && resp.code === 200) {
            if (resp?.data?.trigger_rule) {
                setRules(resp.data.trigger_rule.configuration);
                action.fields.name.value = resp.data.trigger_rule.name;
                action.fields.description.value = resp.data.trigger_rule.description;
                action.fields.id = resp.data.trigger_rule.id;
                setValue(1);
            } else {
                let selectedRule = responseList?.data.find(x => x.id === resp.data.trigger_rule_id)
                if (selectedRule) {
                    setSelect(selectedRule) // resp?.data?.trigger_rule_id
                }
                setValue(0);
            }
            setTriggerProfile(resp.data);
        }
    };

    const getAPIKey = async () => {
        let response = await UserService.getAPIKey();
        if (response?.status) {
            return response?.data?.apiKey;
        }
        return "";
    }

    // const getSettings = async () => {
    //     let port;
    //     const result = await settingsService.getSettings();
    //     if (result && result.code === 200) {
    //         port = result.data.service_https_port ? result.data.service_https_port : '2096';
    //     }
    //
    //     return `${window.location.protocol}//${window.location.hostname}:${port}/api/v1/${props.webhook}/run/${props.automId}`
    // }


    const handleClose = () => {
        props.setOpen(false);
    };

    const handleChange = (newValue) => {
        setValue(newValue);
    };

    const handleNewData = (isPrivate) => {
        let newData = {
            autom_id: props.automId,
            configuration: rules,
            period: "",
            type: "WEBHOOK",
            private: isPrivate,
            name: action.fields.name.value.trim(),
            description: action.fields.description.value,
            tab: "custom",
        };

        if (triggerProfile?.trigger_profile_id) {
            newData.trigger_profile_id = triggerProfile.trigger_profile_id
        }
        if (triggerProfile.id) {
            newData.trigger_profile_id = triggerProfile.id
        }
        if (triggerProfile.trigger_rule?.id) {
            newData.trigger_rule_id = triggerProfile.trigger_rule?.id
        }

        return newData;
    };

    const handleSave = async () => {
        let response = { status: true }
        let trigger_profile_id = triggerProfile?.trigger_profile_id ? triggerProfile?.trigger_profile_id : triggerProfile?.id;
        let trigger_rule_id = select ? select.id : null;
        if (trigger_profile_id || trigger_rule_id) {
            response = await triggerService.createProfile({
                autom_id: props.automId,
                type: "WEBHOOK",
                trigger_rule_id,
                tab: "static",
                trigger_profile_id
            });
        }

        if (response) {
            if (response?.status) {
                setAlert(alert => ({ ...alert, show: true, message: t("CONTENT.WEBHOOK.ADD-SUCCESS"), severity: 'success' }))
                props.setOpen(false);
            }
        }
    };

    const handleCreate = async (isPrivate) => {
        if (!action.fields.name) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t('CONTENT.WEBHOOK.RULE-NAME-MANDATORY'),
                severity: 'error'
            }));
            return;
        } else if (action.fields.name.value.trim().length < 3) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t('CONTENT.WEBHOOK.RULE-NAME-LENGTH'),
                severity: 'error'
            }));
            return;
        }

        // if not created before trigger profile
        const response = await triggerService.createProfile(
            handleNewData(isPrivate)
        );
        if (response) {
            setAlert(alert => ({ ...alert, show: true, message: t("CONTENT.WEBHOOK.ADD-SUCCESS"), severity: 'success' }))
        }

        props.setOpen(false);
    }

    const handleFocus = (event) => event.target.select();

    const handleSelectDropdown = (_, newValue) => {
        setSelect(newValue ? newValue : {});
    };

    const handleCopy = () => {
        try {
            const textBox = document.getElementById("auTextInput");
            textBox.select();
            document.execCommand("copy");
            setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.AUTOMS.AUTOM-COPIED'), severity: 'success' }))
        } catch (err) {
            console.error('Fallback: Oops, unable to copy', err);
        }
    }
    const handleDelete = async () => {
        const res = await triggerService.deleteRules([action.fields.id]);
        if (res?.status) {
            setOpen(false);
            props.setOpen(false);
        }
        setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.WEBHOOK." + res.message), severity: res.status ? 'success' : 'error' }))
    }
    return (
        <Dialog
            id="autom-addNew-modal"
            className="user-detail-dialog"
            data-test="autom-add-new"
            open={true}
            onClose={handleClose}
            PaperProps={{
                style: {
                    overflowX: "hidden",
                    minWidth: 400,
                },
            }}>
            <div className="db-tabs db-page">
                {/* <button className={props.tab === 0 ? 'db-tabactive' : ''} value="license"
                    onClick={() => props.onTabChange("email")}>
                    {t("CONTENT.EMAIL-TRIGGER.EMAIL-CONFIGURATION")}
                </button> */}
                <button className={props.tab === 1 ? 'db-tabactive' : ''} value="about"
                    onClick={() => props.onTabChange("webhook")}>
                    {t("CONTENT.WEBHOOK.ADD-RULE")}
                </button>
            </div>
            <DialogContent style={{ paddingLeft: 30, paddingRight: 10 }}>
                <div className="form-element">
                    <div className="condition-tabs" style={{ display: 'inline' }}>
                        <button
                            className={value === 0 ? "condition-active" : ""}
                            onClick={() => handleChange(0)}
                        >
                            {t("CONTENT.WEBHOOK.STATIC")}
                        </button>
                        <button
                            className={value === 1 ? "condition-active" : ""}
                            onClick={() => handleChange(1)}
                        >
                            {t("CONTENT.WEBHOOK.CUSTOM")}
                        </button>
                    </div>
                    {/* <IconButton size="small" onClick={() => setOpen(true)}
                                style={{ float: 'right', marginTop: 5, marginRight: 30 }}>
                        <Delete fontSize="small" />
                    </IconButton> */}
                    <TabPanel value={value} index={0}>
                        <p>{t("CONTENT.WEBHOOK.URL")}</p>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ flexBasis: "70%" }}>
                                <TextFieldInput
                                    onFocus={handleFocus}
                                    value={webhookUrl}
                                    width="100%"
                                />
                            </div>
                            <div style={{ width: 32, cursor: 'pointer', marginLeft: 5 }}>
                                <Tooltip title={t("CONTENT.WEBHOOK.COPY")}>
                                    <InsertLinkIcon onClick={handleCopy} />
                                </Tooltip>
                            </div>
                        </div>
                        <div className="form-element">
                            <p>Choose Trigger Rule Set</p>
                            <div className="form-element">

                                <Autocomplete
                                    value={select}
                                    onChange={(e, newValue) => handleSelectDropdown(e, newValue)}
                                    id="webhook-rule-options"
                                    style={{ width: 300, position: 'relative', background: '#FBFBFB' }}
                                    size="small"
                                    options={ruleList}
                                    classes={{
                                        option: classes.option,
                                    }}
                                    autoHighlight
                                    getOptionLabel={(option) => option.name ?? ""}
                                    getOptionSelected={(option, value) => option.id === value?.id}
                                    renderOption={(option) => (
                                        <>
                                            {option.name}
                                        </>
                                    )}
                                    renderInput={(params) => (
                                        <>
                                            <TextField
                                                {...params}
                                                className="select-action-input"
                                                placeholder="No Trigger Rule Selected"
                                                variant="outlined"
                                                inputProps={{
                                                    ...params.inputProps,
                                                    "aria-autocomplete": 'new-password',
                                                    autoComplete: 'new-password', // disable autocomplete and autofill
                                                    style: {
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        color: '#46474E'
                                                    }
                                                }}
                                            />
                                        </>
                                    )}
                                />
                            </div>
                        </div>
                    </TabPanel>
                    <TabPanel value={value} index={1}>
                        <WebHookForm
                            action={action}
                            rules={rules}
                            setRules={setRules}
                            setOpenVariable={props.setOpenVariable}
                            isVariableAdd={true}
                            variables={props.variables}
                        />
                    </TabPanel>
                </div>
                {open && <DeleteWebhook setOpen={setOpen} deleteFunc={handleDelete} setRefresh={() => { }} />}
            </DialogContent>
            <DialogActions
                style={{
                    justifyContent: value === 0 ? "center" : "space-between",
                    padding: "8px 24px 16px 24px",
                }}
            >
                {value === 0 ? (
                    <>
                        <Button
                            onClick={handleClose}
                            style={{
                                background: "#D75E5E",
                                color: "#fff",
                                borderRadius: 4,
                                boxShadow: "0px 0px 8px -3px rgba(0, 0, 0, 0.25);",
                                minWidth: 130,
                            }}
                        >
                            {t("CONTENT.AUTOMS-LIST.CLOSE")}
                        </Button>
                        <Button
                            onClick={handleSave}
                            style={{
                                background: "#162436",
                                color: "#fff",
                                borderRadius: 4,
                                boxShadow: "0px 0px 8px -3px rgba(0, 0, 0, 0.25);",
                                minWidth: 130,
                            }}
                        >
                            {t("CONTENT.AUTOMS-LIST.SAVE")}
                        </Button>
                    </>
                ) : (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                        }}
                    >
                        <Button
                            onClick={() => handleCreate(false)}
                            disabled={rules.length < 1}
                            style={{
                                background: "#162436",
                                color: "#fff",
                                borderRadius: 4,
                                boxShadow: "0px 0px 8px -3px rgba(0, 0, 0, 0.25);",
                                minWidth: 150,
                                opacity: rules.length < 1 ? .6 : 1
                            }}
                        >
                            {t("CONTENT.WEBHOOK.CREATE-USE")}
                        </Button>
                        <Button
                            onClick={() => handleCreate(true)}
                            disabled={rules.length < 1}
                            style={{
                                background: "white",
                                color: "#162436",
                                border: "1px solid #162436",
                                borderRadius: 4,
                                boxShadow: "0px 0px 8px -3px rgba(0, 0, 0, 0.25);",
                                minWidth: 150,
                                opacity: rules.length < 1 ? .6 : 1
                            }}
                        >
                            {t("CONTENT.WEBHOOK.ONLY-USE")}
                        </Button>
                    </div>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default WebHook;
